import { useEffect, useState } from 'react'

/**
 * Three-mode color theme:
 *   - system: follow `prefers-color-scheme`
 *   - light: force light
 *   - dark:  force dark
 *
 * Implementation: toggles the `dark` class on <html> for Tailwind's
 * darkMode: 'class' to pick up. Stored in localStorage so refreshes
 * + new tabs honor the choice. The initial render in `main.tsx` is
 * a separate inline-script-free path; this hook reconciles on mount
 * which is good enough — there's a brief unstyled flash only when
 * the user is OS-dark + has chosen "light" or vice versa, and
 * resolving that needs an early-render script (CSP-allowed with a
 * nonce, deferred).
 */

export type ThemeMode = 'system' | 'light' | 'dark'

const LS_KEY = 'crawfish.budget.theme'

function readStored(): ThemeMode {
  try {
    const v = localStorage.getItem(LS_KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch {
    // localStorage failures (private mode, quota) → fall through.
  }
  return 'system'
}

function effective(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'light') return 'light'
  if (mode === 'dark') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function applyTheme(mode: ThemeMode) {
  const e = effective(mode)
  document.documentElement.classList.toggle('dark', e === 'dark')
}

export function useTheme(): {
  mode: ThemeMode
  setMode: (m: ThemeMode) => void
} {
  const [mode, setMode] = useState<ThemeMode>(readStored)

  useEffect(() => {
    applyTheme(mode)
    try {
      localStorage.setItem(LS_KEY, mode)
    } catch {
      // Persistence is best-effort.
    }
  }, [mode])

  // When in `system` mode, watch the OS preference so the page
  // reacts to OS-level theme changes without a refresh.
  useEffect(() => {
    if (mode !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyTheme('system')
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [mode])

  return { mode, setMode }
}
